import type { ReactElement } from "react";
import type { UserRole } from "@shared/core/types";
import type { IconName } from "./components/ui/icon";

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  adminOnly?: boolean;
  /** Rôles qui voient l'entrée (admin passe toujours). Absent = tout le monde. */
  roles?: readonly UserRole[];
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
  roles?: readonly UserRole[];
  /**
   * Rang dans la barre latérale (croissant). « Principal » vaut 0, « Système »
   * 100, un module 50 par défaut : le module central au métier d'un projet peut
   * ainsi passer devant les autres sans toucher au noyau.
   */
  order?: number;
}

export interface AppRoute {
  path: string;
  element: ReactElement;
  /** Un non-admin qui tape l'URL est renvoyé à l'accueil (l'API refuse de toute façon). */
  adminOnly?: boolean;
  /** Rôles autorisés (admin passe toujours). Absent = tout compte connecté. */
  roles?: readonly UserRole[];
}

export interface HomeWidget {
  id: string;
  element: ReactElement;
  adminOnly?: boolean;
  roles?: readonly UserRole[];
  /** « wide » : le widget occupe toute la largeur de la grille d'accueil. */
  size?: "wide";
}

/** Un élément (route, entrée de menu, widget) est-il visible pour ce rôle ? */
export function allowedFor(
  item: { adminOnly?: boolean; roles?: readonly UserRole[] },
  role: UserRole | undefined,
): boolean {
  if (!role) return false;
  if (role === "admin") return true;
  if (item.adminOnly) return false;
  if (item.roles && !item.roles.includes(role)) return false;
  return true;
}

/**
 * Contrat d'un module côté web. Le registre `src/modules/index.ts` (généré par
 * `pnpm blueprint apply`) liste les modules actifs ; le noyau assemble leurs
 * routes, leur navigation, leurs widgets d'accueil et leur emplacement dans la
 * barre du haut.
 */
export interface WebModule {
  name: string;
  /** Pages dans le shell (connecté). */
  routes: AppRoute[];
  /** Pages hors shell, sans connexion (ex. lien sécurisé pour un avocat). */
  publicRoutes?: AppRoute[];
  /** Groupes ajoutés à la barre latérale, entre « Principal » et « Système ». */
  nav: NavGroup[];
  homeWidgets?: HomeWidget[];
  /** Élément rendu à gauche de la barre du haut (ex. recherche globale). */
  topbar?: ReactElement;
}
